'use client';

import type { ReactNode } from 'react';
import { METRICS_TIME_RANGES, type MetricsTimeRangeKey } from '@/lib/metrics-time-range';
import { cn } from '@/lib/utils';

function toDatetimeLocal(ms: number): string {
  const date = new Date(ms);
  const pad = (value: number) => String(value).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function MetricsTimeRangeControls({
  fromMs,
  toMs,
  onSetFromTo,
  onSetPreset,
  activePresetLabel,
  toDisabled = false,
  onSetNow,
  rangeActions,
  children,
}: {
  fromMs: number;
  toMs: number;
  onSetFromTo: (fromMs: number, toMs: number) => void;
  onSetPreset: (key: MetricsTimeRangeKey) => void;
  activePresetLabel?: string | null;
  toDisabled?: boolean;
  onSetNow?: () => void;
  rangeActions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <div
        role="group"
        aria-label="Time window presets"
        className="flex h-9 items-center rounded-md border bg-background p-0.5"
      >
        <span className="px-1.5 text-[11px] text-muted-foreground">Window</span>
        <div className="flex border-l pl-0.5">
          {METRICS_TIME_RANGES.map((range) => (
            <button
              key={range.key}
              type="button"
              title={range.label}
              aria-pressed={activePresetLabel === range.label}
              onClick={() => onSetPreset(range.key)}
              className={cn(
                'rounded px-2 py-1 text-xs transition-colors',
                activePresetLabel === range.label
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted',
              )}
            >
              {range.shortLabel}
            </button>
          ))}
        </div>
      </div>

      <div
        role="group"
        aria-label="Custom time range"
        className="flex h-9 items-center rounded-md border bg-background p-0.5"
      >
        <label className="flex items-center gap-1 px-1.5 text-[11px] text-muted-foreground">
          From
          <input
            aria-label="From time"
            type="datetime-local"
            className="h-7 bg-transparent px-1 text-xs text-foreground outline-none"
            value={toDatetimeLocal(fromMs)}
            onChange={(event) => {
              const nextFromMs = new Date(event.target.value).getTime();
              if (Number.isFinite(nextFromMs)) onSetFromTo(nextFromMs, toMs);
            }}
          />
        </label>

        <label className="flex items-center gap-1 border-l px-1.5 text-[11px] text-muted-foreground">
          To
          <input
            aria-label="To time"
            type="datetime-local"
            className="h-7 bg-transparent px-1 text-xs text-foreground outline-none disabled:opacity-50"
            value={toDatetimeLocal(toMs)}
            onChange={(event) => {
              const nextToMs = new Date(event.target.value).getTime();
              if (Number.isFinite(nextToMs)) onSetFromTo(fromMs, nextToMs);
            }}
            disabled={toDisabled}
          />
        </label>

        <button
          type="button"
          title="Move time window to now"
          disabled={toDisabled}
          onClick={() => {
            if (onSetNow) {
              onSetNow();
              return;
            }
            const now = Date.now();
            onSetFromTo(now - Math.max(0, toMs - fromMs), now);
          }}
          className="h-7 border-l px-2 text-xs text-foreground hover:bg-muted disabled:opacity-50"
        >
          Now
        </button>

        {rangeActions ? <div className="flex items-center border-l pl-0.5">{rangeActions}</div> : null}
      </div>

      {children}
    </div>
  );
}
